import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { Map as MapIcon, Plus } from "lucide-react";

// SpinHouse roadmap: big branches of work flow into one system. Each branch
// holds features with who works on them and what result we expect. The whole
// board is one document (GET/PUT /roadmap), private to ROADMAP_EMAILS — see
// ROADMAP_EMAILS in server.py.

const STATUS = {
  now: { label: "Сейчас", cls: "bg-emerald-500 text-zinc-950 border-emerald-500" },
  next: { label: "Дальше", cls: "text-emerald-400 border-emerald-500/50" },
  later: { label: "Потом", cls: "text-zinc-400 border-zinc-600" },
  done: { label: "Готово", cls: "text-sky-400 border-sky-500/50" },
};
const ORDER = { now: 0, next: 1, later: 2, done: 3 };

// Tailwind needs full class names in the source, so each branch color is spelled out.
const COLORS = {
  violet: { label: "Фиолетовый", branch: "bg-violet-500/15 text-violet-400", line: "bg-violet-400", bar: "before:bg-violet-400", av: "bg-violet-500/25 text-violet-400" },
  teal: { label: "Бирюзовый", branch: "bg-teal-500/15 text-teal-400", line: "bg-teal-400", bar: "before:bg-teal-400", av: "bg-teal-500/25 text-teal-400" },
  orange: { label: "Оранжевый", branch: "bg-orange-500/15 text-orange-400", line: "bg-orange-400", bar: "before:bg-orange-400", av: "bg-orange-500/25 text-orange-400" },
  pink: { label: "Розовый", branch: "bg-pink-500/15 text-pink-400", line: "bg-pink-400", bar: "before:bg-pink-400", av: "bg-pink-500/25 text-pink-400" },
  zinc: { label: "Серый", branch: "bg-zinc-500/15 text-zinc-400", line: "bg-zinc-400", bar: "before:bg-zinc-400", av: "bg-zinc-500/25 text-zinc-400" },
  sky: { label: "Синий", branch: "bg-sky-500/15 text-sky-400", line: "bg-sky-400", bar: "before:bg-sky-400", av: "bg-sky-500/25 text-sky-400" },
};
const color = (c) => COLORS[c] || COLORS.zinc;
const initials = (n) => String(n).trim().split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
const selectCls = "h-9 w-full rounded-md border border-zinc-700 bg-zinc-900 px-2 text-sm text-zinc-400";

function Field({ label, className, children }) {
  return (
    <label className={cn("flex flex-col gap-1 text-xs text-zinc-400", className)}>
      {label}
      {children}
    </label>
  );
}

function Editor({ edit, doc, onSave, onDelete, onCancel }) {
  const branch = edit.type === "branch" ? doc.branches.find((b) => b.id === edit.id) : null;
  const item = edit.type === "item" ? doc.items.find((i) => i.id === edit.id) : null;
  const init =
    edit.type === "system" ? { ...doc.system }
    : edit.type === "branch" ? { name: "", sub: "", color: "zinc", ...branch }
    : { title: "", branch: edit.branch, status: "next", expect: "", ...item, who: (item?.who || []).join(", ") };
  const [f, setF] = useState(init);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const exists = edit.type === "branch" ? !!branch : edit.type === "item" ? !!item : false;
  const [armed, setArmed] = useState(false);

  return (
    <form
      className="grid gap-3 rounded-xl border border-emerald-500/40 bg-zinc-900/80 p-4 sm:grid-cols-3"
      onSubmit={(e) => { e.preventDefault(); onSave(f); }}
    >
      {edit.type === "system" && (
        <>
          <Field label="Название системы"><Input value={f.name || ""} onChange={set("name")} required /></Field>
          <Field label="Куда идём" className="sm:col-span-2"><Input value={f.goal || ""} onChange={set("goal")} /></Field>
        </>
      )}
      {edit.type === "branch" && (
        <>
          <Field label="Ветка"><Input value={f.name} onChange={set("name")} required /></Field>
          <Field label="Подпись"><Input value={f.sub} onChange={set("sub")} /></Field>
          <Field label="Цвет">
            <select className={selectCls} value={f.color} onChange={set("color")}>
              {Object.entries(COLORS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </Field>
        </>
      )}
      {edit.type === "item" && (
        <>
          <Field label="Функция" className="sm:col-span-3"><Input value={f.title} onChange={set("title")} required /></Field>
          <Field label="Ветка">
            <select className={selectCls} value={f.branch} onChange={set("branch")}>
              {doc.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
          <Field label="Когда">
            <select className={selectCls} value={f.status} onChange={set("status")}>
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </Field>
          <Field label="Участники, через запятую"><Input value={f.who} onChange={set("who")} /></Field>
          <Field label="Что ожидаем (результат)" className="sm:col-span-3">
            <Input value={f.expect} onChange={set("expect")} placeholder="Что изменится, когда функция будет готова" />
          </Field>
        </>
      )}
      <div className="flex flex-wrap gap-2 sm:col-span-3">
        <Button type="submit">Сохранить</Button>
        <Button type="button" variant="outline" onClick={onCancel}>Отмена</Button>
        {exists && (
          <Button type="button" variant="outline" className="text-red-400"
            onClick={() => (armed ? onDelete() : setArmed(true))}>
            {armed ? "Точно удалить?" : edit.type === "branch" ? "Удалить ветку" : "Удалить функцию"}
          </Button>
        )}
      </div>
    </form>
  );
}

function FeatureCard({ item, c, onClick }) {
  const st = STATUS[item.status] || STATUS.next;
  const who = item.who || [];
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative flex w-full flex-col gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2.5 text-left transition-colors hover:border-zinc-600 sm:w-56",
        "before:absolute before:bottom-2 before:left-0 before:top-2 before:w-[3px] before:rounded-r",
        c.bar,
        item.status === "later" && "opacity-70",
      )}
    >
      <span className={cn("w-fit rounded-full border px-2 py-px text-[11px] font-semibold", st.cls)}>{st.label}</span>
      <span className={cn("text-sm font-semibold leading-snug text-zinc-400", item.status === "done" && "text-zinc-500 line-through")}>{item.title}</span>
      <span className={cn("text-xs leading-snug", item.expect ? "text-zinc-300" : "italic text-zinc-500")}>
        {item.expect ? `Ждём: ${item.expect}` : "Результат не задан"}
      </span>
      <span className="mt-0.5 flex items-center justify-between gap-2 text-[11px] text-zinc-500">
        <span className="flex">
          {who.map((w, i) => (
            <span key={w} title={w} className={cn("flex h-[22px] w-[22px] items-center justify-center rounded-full border-2 border-zinc-900 text-[10px] font-semibold", c.av, i && "-ml-1.5")}>
              {initials(w)}
            </span>
          ))}
        </span>
        <span className="truncate">{who.join(", ") || "нет участников"}</span>
      </span>
    </button>
  );
}

export default function RoadmapPage() {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [edit, setEdit] = useState(null);

  useEffect(() => {
    api.get("/roadmap")
      .then((r) => setDoc(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Не удалось загрузить роадмап"));
  }, []);

  const save = async (next) => {
    try {
      const r = await api.put("/roadmap", { system: next.system, branches: next.branches, items: next.items });
      setDoc(r.data);
      setEdit(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Не сохранилось");
    }
  };

  const onSave = (f) => {
    const next = { ...doc, branches: [...doc.branches], items: [...doc.items] };
    if (edit.type === "system") next.system = { ...doc.system, ...f };
    else if (edit.type === "branch") {
      const i = next.branches.findIndex((b) => b.id === edit.id);
      if (i >= 0) next.branches[i] = { ...next.branches[i], ...f };
      else next.branches.push({ id: `b${Date.now()}`, ...f });
    } else {
      const p = { ...f, who: String(f.who || "").split(",").map((x) => x.trim()).filter(Boolean) };
      const i = next.items.findIndex((x) => x.id === edit.id);
      if (i >= 0) next.items[i] = { ...next.items[i], ...p };
      else next.items.push({ id: `r${Date.now()}`, ...p });
    }
    save(next);
  };

  const onDelete = () => {
    const next = { ...doc };
    if (edit.type === "branch") {
      next.branches = doc.branches.filter((b) => b.id !== edit.id);
      next.items = doc.items.filter((i) => i.branch !== edit.id);
    } else next.items = doc.items.filter((i) => i.id !== edit.id);
    save(next);
  };

  const open = (e) => { setEdit(e); window.scrollTo({ top: 0, behavior: "smooth" }); };

  if (error) return <div className="p-6 text-sm text-zinc-400">{error}</div>;
  if (!doc) return <div className="p-6 text-sm text-zinc-500">Загружаю…</div>;

  const count = (k) => doc.items.filter((i) => i.status === k).length;
  const noExpect = doc.items.filter((i) => !i.expect && i.status !== "done").length;

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-zinc-400"><MapIcon className="h-6 w-6 text-emerald-400" />Роадмап SpinHouse</h1>
          <p className="mt-1 text-sm text-zinc-400">Куда идём, какие функции делаем, кто участвует и что ждём. Нажми на функцию или ветку, чтобы изменить.</p>
        </div>
        <div className="flex gap-5 font-mono text-xs text-zinc-500">
          {[["now", "сейчас", "text-emerald-400"], ["next", "дальше", "text-zinc-400"], ["later", "потом", "text-zinc-400"]].map(([k, l, cl]) => (
            <div key={k}><b className={cn("block text-xl font-medium", cl)}>{count(k)}</b>{l}</div>
          ))}
          <div><b className="block text-xl font-medium text-orange-400">{noExpect}</b>без результата</div>
        </div>
      </div>

      {edit && <Editor key={JSON.stringify(edit)} edit={edit} doc={doc} onSave={onSave} onDelete={onDelete} onCancel={() => setEdit(null)} />}

      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_200px]">
        <div className="flex min-w-0 flex-col gap-3">
          {doc.branches.map((b) => {
            const c = color(b.color);
            const items = doc.items.filter((i) => i.branch === b.id).sort((x, y) => ORDER[x.status] - ORDER[y.status]);
            return (
              <div key={b.id} className="grid items-center gap-3 xl:grid-cols-[200px_minmax(0,max-content)_minmax(40px,1fr)]">
                <button type="button" onClick={() => open({ type: "branch", id: b.id })}
                  className={cn("flex flex-col justify-center self-stretch rounded-xl px-3 py-2.5 text-left", c.branch)}>
                  <span className="text-sm font-semibold">{b.name}</span>
                  <span className="text-xs opacity-80">{b.sub}</span>
                </button>
                <div className="flex flex-wrap items-stretch gap-2">
                  {items.map((i) => <FeatureCard key={i.id} item={i} c={c} onClick={() => open({ type: "item", id: i.id })} />)}
                  <button type="button" onClick={() => open({ type: "item", branch: b.id })}
                    className="flex items-center gap-1 rounded-lg border border-dashed border-zinc-700 px-3 py-2 text-xs text-zinc-500 hover:border-emerald-500 hover:text-emerald-400">
                    <Plus className="h-3.5 w-3.5" />функция
                  </button>
                </div>
                <div className={cn("hidden h-0.5 rounded xl:block", c.line)} />
              </div>
            );
          })}
          <div>
            <button type="button" onClick={() => open({ type: "branch" })}
              className="inline-flex items-center gap-1 rounded-lg border border-dashed border-zinc-700 px-3 py-2 text-xs text-zinc-500 hover:border-emerald-500 hover:text-emerald-400">
              <Plus className="h-3.5 w-3.5" />ветка
            </button>
          </div>
        </div>
        <button type="button" onClick={() => open({ type: "system" })}
          className="rounded-2xl border-2 border-emerald-500 bg-zinc-900 p-4 text-center">
          <div className="sticky top-[30vh] flex flex-col gap-2">
            <span className="text-base font-semibold text-zinc-400">{doc.system?.name || "Единая система"}</span>
            <span className="text-xs text-zinc-400">{doc.system?.goal || "куда всё сходится"}</span>
          </div>
        </button>
      </div>
    </div>
  );
}
